/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  SignInLogger,
  SignInResult,
  type SignInFlowName,
  type SignInStepName
} from "../../../model/logging/SignInLogger";

/**
 * In-memory timeline for one sign-in attempt.
 *
 * `signInEnd` needs facts gathered across the whole flow — which auth types
 * were already tried, how long each step took, where it stopped — and none of
 * that is state the UI renders, so it lives here rather than in the Redux
 * slice.
 *
 * One transaction per *flow leg*, not per user-visible attempt: opening it
 * when the orchestrator merely resolves an interactive auth type would fold
 * however long the user spends typing into `duration_ms`. A fallback chain
 * stays visible through `attempted_types` (seeded from the store at open) and
 * the separate `signInFallback` operational event.
 */

/** Reason recorded when a new attempt starts while one is still open. */
const CANCEL_REASON_SUPERSEDED: string = "superseded";

/** Options for {@link beginSignInAttempt}. */
export interface TBeginSignInAttempt {
  flow: SignInFlowName;
  authType: string;
  /** True when this device has no auth context persisted from a prior sign-in. */
  isFirstSignIn: boolean;
  /**
   * True when the user explicitly triggered this sign-in (typed credentials,
   * scanned a code); false for silent paths — IP auto-login, session restore.
   * Maps to the spec's `user_initiated_auth`.
   */
  isUserInitiated: boolean;
  msoName: string;
  /** Auth types the orchestrator already tried and gave up on, if any. */
  attemptedTypes?: string[];
}

interface SignInAttempt {
  transactionId: string;
  startedAtMs: number;
  flow: SignInFlowName;
  authType: string;
  isFirstSignIn: boolean;
  isUserInitiated: boolean;
  attemptedTypes: string[];
  stepTimingsMs: Record<string, number>;
  stepReached: SignInStepName | undefined;
}

let currentAttempt: SignInAttempt | null = null;

/** Maps a step to the `t_<step>_ms` field name used on signInEnd. */
function stepTimingKey(step: SignInStepName): string {
  return "t_" + step + "_ms";
}

/**
 * Opens a sign-in attempt and emits `signInStart`.
 *
 * If an attempt is already open it is cancelled first: the orchestration walk
 * can restart the flow, and leaking the previous transaction would leave a
 * start with no end on the dashboard.
 */
export function beginSignInAttempt(options: TBeginSignInAttempt): string {
  if (currentAttempt !== null) {
    SignInLogger.logSignInCancelled(
      currentAttempt.transactionId,
      CANCEL_REASON_SUPERSEDED,
      { flow: currentAttempt.flow, auth_type: currentAttempt.authType }
    );
    currentAttempt = null;
  }

  const transactionId: string = SignInLogger.logSignInStart({
    flow: options.flow,
    auth_type: options.authType,
    is_first_sign_in: String(options.isFirstSignIn),
    mso_name: options.msoName
  });

  currentAttempt = {
    transactionId: transactionId,
    startedAtMs: Date.now(),
    flow: options.flow,
    authType: options.authType,
    isFirstSignIn: options.isFirstSignIn,
    isUserInitiated: options.isUserInitiated,
    attemptedTypes:
      options.attemptedTypes === undefined ? [] : [...options.attemptedTypes],
    stepTimingsMs: {},
    stepReached: undefined
  };
  return transactionId;
}

/** True while an attempt is open. */
export function isSignInAttemptOpen(): boolean {
  return currentAttempt !== null;
}

/** Records how long one step took, and that the flow reached it. */
export function markStep(step: SignInStepName, latencyMs: number): void {
  if (currentAttempt === null) {
    return;
  }
  currentAttempt.stepTimingsMs[stepTimingKey(step)] = latencyMs;
  currentAttempt.stepReached = step;
}

/** Records the step the flow is entering, before its latency is known. */
export function markStepReached(step: SignInStepName): void {
  if (currentAttempt === null) {
    return;
  }
  currentAttempt.stepReached = step;
}

/** Records an auth type the orchestrator tried, for `attempted_types`. */
export function markAttemptedType(authType: string): void {
  if (currentAttempt === null) {
    return;
  }
  if (currentAttempt.attemptedTypes.indexOf(authType) === -1) {
    currentAttempt.attemptedTypes.push(authType);
  }
}

/**
 * Closes the attempt and emits `signInEnd` with the assembled timeline.
 *
 * Safe to call when no attempt is open — a flow can reject before
 * {@link beginSignInAttempt} runs — it is then a no-op, so callers do not
 * have to guard their rejection paths.
 */
export function endSignInAttempt(
  result: string,
  errorCode?: string,
  stepReached?: SignInStepName
): void {
  if (currentAttempt === null) {
    return;
  }
  const attempt: SignInAttempt = currentAttempt;
  // Clear first so a throw inside the logger cannot leave a stale attempt
  // that a later call would close a second time.
  currentAttempt = null;

  const finalStep: SignInStepName | undefined =
    stepReached !== undefined ? stepReached : attempt.stepReached;

  const fields: Record<string, string | undefined> = {
    result: result,
    flow: attempt.flow,
    auth_type: attempt.authType,
    duration_ms: String(Date.now() - attempt.startedAtMs),
    step_reached: finalStep,
    error_code: errorCode,
    attempted_types:
      attempt.attemptedTypes.length > 0
        ? attempt.attemptedTypes.join(",")
        : undefined
  };

  const timingKeys: string[] = Object.keys(attempt.stepTimingsMs);
  for (let index = 0; index < timingKeys.length; index += 1) {
    fields[timingKeys[index]] = String(
      attempt.stepTimingsMs[timingKeys[index]]
    );
  }

  SignInLogger.logSignInEnd(attempt.transactionId, fields);

  // The spec's signIn analytics event is emitted from here, not from the
  // thunks, so there is exactly one per user-visible attempt no matter how
  // many auth types the orchestrator worked through internally.
  SignInLogger.logSignInAnalytics({
    status: result,
    login_type: SignInLogger.toSpecLoginType(attempt.authType),
    auth_type: attempt.authType,
    user_initiated_auth: String(attempt.isUserInitiated),
    is_first_sign_in: String(attempt.isFirstSignIn),
    duration_ms: String(Date.now() - attempt.startedAtMs),
    error_code: errorCode
  });
}

/** Convenience wrapper for the success path. */
export function completeSignInAttempt(): void {
  endSignInAttempt(SignInResult.Success);
}

/** Convenience wrapper for the failure path. */
export function failSignInAttempt(
  errorCode?: string,
  stepReached?: SignInStepName
): void {
  endSignInAttempt(SignInResult.Failure, errorCode, stepReached);
}

/**
 * Abandons the attempt without an outcome — navigation away, app
 * backgrounded, or the abort signal fired mid-flow.
 */
export function cancelSignInAttempt(reason: string): void {
  if (currentAttempt === null) {
    return;
  }
  const attempt: SignInAttempt = currentAttempt;
  currentAttempt = null;
  SignInLogger.logSignInCancelled(attempt.transactionId, reason, {
    flow: attempt.flow,
    auth_type: attempt.authType,
    duration_ms: String(Date.now() - attempt.startedAtMs)
  });
}

/** Reset module state — test-only. */
export function resetSignInTelemetryForTest(): void {
  currentAttempt = null;
}
